import React from 'react'
import { ColorThemeProps } from '@grupoboticario/flora'
import { styled, CSS, forwardRef, FloraVariants } from '@grupoboticario/flora-react-system'
import { filterUndefined, HTMLFloraProps } from '@grupoboticario/flora-react-utils'

const DEFAULT_TAG = 'h1'

const setFontStyle = (
  fontFamily: string,
  fontSize: string,
  lineHeight: string,
  desktop: CSS,
): CSS => ({
  fontFamily,
  fontSize,
  lineHeight,
  '@desktop': desktop,
})

const StyledHeading = styled(DEFAULT_TAG, {
  margin: '0',
  padding: '0',
  variants: {
    level: {
      1: setFontStyle('$primary', '4rem', '72px', { fontSize: '6rem', lineHeight: '104px' }),
      2: setFontStyle('$primary', '3rem', '56px', { fontSize: '4.5rem', lineHeight: '80px' }),
      3: setFontStyle('$primary', '2.5rem', '48px', { fontSize: '3.5rem', lineHeight: '64px' }),
      4: setFontStyle('$primary', '2rem', '40px', { fontSize: '2.5rem', lineHeight: '48px' }),
      5: setFontStyle('$secondary', '1.5rem', '32px', { fontSize: '2rem', lineHeight: '40px' }),
      6: setFontStyle('$secondary', '1.25rem', '24px', { fontSize: '1.5rem', lineHeight: '32px' }),
    },
  },
  defaultVariants: {
    level: '1',
  },
})

export type HeadingProps = HTMLFloraProps<'h1'> &
  FloraVariants<typeof StyledHeading> & {
    /**
     * The color of the text
     */
    color?: ColorThemeProps
  }

/**
 * @component
 * @name Heading
 * @description Heading component
 * @tutorial https://flora.gb.tech/docs/components/core/heading
 */
export const Heading = forwardRef<'h1', HeadingProps>(({ color, ...props }, forwardedRef) => {
  let tagName = `h${props.level}` as keyof JSX.IntrinsicElements
  if (!['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
    tagName = DEFAULT_TAG
  }
  const css = filterUndefined({
    ...(props.css as any),
    color,
  })

  return <StyledHeading ref={forwardedRef} data-flora="heading" as={tagName} {...props} css={css} />
})
